import { useCallback } from "react";
import { invariant } from "@pexip-engage/utils";
import { json, type LoaderFunctionArgs, type SerializeFrom } from "@remix-run/node";
import { useParams } from "@remix-run/react";
import { cacheHeader } from "pretty-cache-header";

import { useSessionIdHeader } from "~/hooks/useFetch";
import { createServerStateManager } from "~/modules/StateManager.server";
import { getPlaceAutocomplete } from "~/services/google.server";
import { getEnterpriseInfo } from "~/services/sdk-api/enterprise.server";
import getCountryCoordinates from "~/utils/getCountryCoordinates";

export interface PlaceAutocompleteLoaderData extends SerializeFrom<typeof loader> {}

export async function loader(args: LoaderFunctionArgs) {
  return args.context.measurer.time(
    "$enterprise/place-autocomplete",
    async function placeAutocompleteLoader() {
      const stateManager = await createServerStateManager(args);

      const sessiontoken = stateManager.request.headers.get("X-Maps-Session-Id");

      const searchParams = new URL(args.request.url).searchParams;
      const countries = searchParams.getAll("countries");
      const input = searchParams.get("input");

      const config = await stateManager.getRequestConfig();

      const enterpriseInfo = await getEnterpriseInfo(config);

      // TODO: I think this is incorrect behaviour with the location stuff
      const defaultCountry = enterpriseInfo?.country;
      invariant(defaultCountry, "The default enterprise country is required");
      const location = getCountryCoordinates(defaultCountry);

      if (input && sessiontoken) {
        const suggestions = await getPlaceAutocomplete({
          language: stateManager.locale,
          countries: countries || [defaultCountry],
          input,
          sessiontoken,
          location,
          config,
        });

        return json(suggestions, CACHE_HEADERS);
      }

      return json([], CACHE_HEADERS);
    },
  );
}

export function useFetchPlaceAutocomplete(sessionToken: string) {
  const { enterprise } = useParams<"enterprise">();
  const sessionIdHeaders = useSessionIdHeader();

  return useCallback(
    async ({
      countries,
      input,
      signal,
    }: {
      input: string;
      countries: string[];
      signal: RequestInit["signal"];
    }) => {
      const searchParams = new URLSearchParams({ input });
      countries.forEach((country) => searchParams.append("countries", country));

      const url = `/api/enterprises/${enterprise}/place-autocomplete?${searchParams}`;

      const headers = new Headers(sessionIdHeaders);
      headers.append("X-Maps-Session-Id", sessionToken);

      let res = await fetch(url, { headers, signal });

      let json: PlaceAutocompleteLoaderData = await res.json();

      return json;
    },
    [enterprise, sessionIdHeaders, sessionToken],
  );
}

const CACHE_HEADERS = {
  headers: {
    "Cache-Control": cacheHeader({ public: true, maxAge: "5minutes" }),
  },
};
